import { parse } from '@conform-to/zod';
import {
	json,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node';
import { z } from 'zod';

import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { SignupEmail } from '#app/emails/signup.server.tsx';
import { validateCSRF } from '#app/utils/csrf.server.ts';
import { prisma } from '#app/utils/db.server.ts';
import { sendEmail } from '#app/utils/email.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { buildMetaTitle } from '#app/utils/meta.ts';
import { getEzLogo } from '#app/utils/misc.tsx';
import { EmailSchema } from '#app/utils/user-validation.ts';

import { prepareVerification } from './verify.tsx';

const SignupSchema = z.object({
	email: EmailSchema,
});

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData();
	await validateCSRF(formData, request.headers);
	checkHoneypot(formData);
	const submission = await parse(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email },
				select: { id: true },
			});
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				});
				return;
			}
		}),
		async: true,
	});
	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const);
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 });
	}
	const { email } = submission.value;
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	});

	const response = await sendEmail({
		to: email,
		subject: `Willkommen im MeinTraktor HändlerLogin!`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	});

	if (response.status === 'success') {
		return redirect(redirectTo.toString());
	} else {
		submission.error[''] = [response.error.message];
		return json({ status: 'error', submission } as const, { status: 500 });
	}
}

export const meta: MetaFunction = () => {
	return [{ title: buildMetaTitle('Registrierung') }];
};

export default function SignupRoute() {
	// const actionData = useActionData<typeof action>();
	// const isPending = useIsPending();
	// const [searchParams] = useSearchParams();
	// const redirectTo = searchParams.get('redirectTo');

	/* const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(SignupSchema),
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			const result = parse(formData, { schema: SignupSchema });
			return result;
		},
		shouldRevalidate: 'onBlur',
	}); */

	return (
		<div className="flex min-h-screen flex-col justify-center bg-gray-100 pb-32 pt-20">
			<div className="mx-auto w-full max-w-md rounded border bg-white py-4">
				<div className="mb-4 flex flex-col gap-3">
					<img
						src={getEzLogo()}
						alt="MeinTraktor Logo"
						className="ml-auto w-[60%] pr-8"
					/>
					<h1 className="pl-8 text-h5" style={{ marginTop: '-1.5em' }}>
						Konto erstellen
					</h1>
					<p className="mt-3 pl-8 text-body-sm text-muted-foreground">
						Die Registrierung ist derzeit nur auf Einladung möglich!
					</p>
				</div>
				{/* <div className="mx-auto mt-4 min-w-full sm:min-w-[368px] max-w-sm">
					<Form method="POST" {...form.props}>
						<AuthenticityTokenInput />
						<HoneypotInputs />
						<Field
							labelProps={{
								htmlFor: fields.email.id,
								children: 'Email',
							}}
							inputProps={{ ...conform.input(fields.email), autoFocus: true, autoComplete: 'email' }}
							errors={fields.email.errors}
						/>
						<ErrorList errors={form.errors} id={form.errorId} />
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : actionData?.status ?? 'idle'}
							type="submit"
							disabled={isPending}
						>
							Absenden
						</StatusButton>
					</Form>

				<ul className="mt-5 flex flex-col gap-5 border-b-2 border-t-2 border-border py-3">
					{providerNames.map(providerName => (
						<li key={providerName}>
							<ProviderConnectionForm
								type="Signup"
								providerName={providerName}
								redirectTo={redirectTo}
							/>
						</li>
					))}
				</div>
				</div>
				*/}
			</div>
		</div>
	);
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />;
}
